import * as React from "react";
import { cn } from "@/lib/utils";

type ButtonVariant = "primary" | "secondary" | "tertiary" | "destructive" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--color-cta)] hover:bg-[var(--color-cta-hover)] active:bg-[var(--color-cta-active)] text-white border border-[var(--color-cta-active)]/40 shadow-[0_4px_12px_rgba(16,185,129,0.25)] hover:shadow-[0_8px_20px_rgba(16,185,129,0.35)] disabled:opacity-50",
  secondary:
    "bg-white hover:bg-emerald-50 text-emerald-700 border border-emerald-700 shadow-sm hover:shadow-md disabled:opacity-50",
  tertiary:
    "border border-[var(--color-border-strong)] bg-white hover:bg-surface-1 text-[var(--color-primary)] shadow-sm hover:shadow-md disabled:opacity-50",
  destructive:
    "bg-[var(--color-signal-rose)] hover:bg-rose-600 active:bg-rose-700 text-white border border-rose-700/40 shadow-sm hover:shadow-md disabled:opacity-50",
  ghost:
    "bg-transparent text-emerald-700 hover:text-emerald-800 hover:underline underline-offset-4 shadow-none disabled:opacity-50",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-11 min-h-[44px] px-3 text-sm",
  md: "h-11 min-h-[44px] px-5 text-base",
  lg: "h-12 min-h-[44px] px-7 text-lg",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      children,
      className,
      type = "button",
      variant = "primary",
      size = "md",
      ...props
    },
    ref,
  ) => {
    const buttonClassName = cn(
      "inline-flex items-center justify-center gap-2 rounded-md font-semibold tracking-normal cursor-pointer overflow-hidden",
      "transition-[background-color,box-shadow] duration-200",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cta)] focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:cursor-not-allowed",
      "active:scale-[0.98]",
      variantClasses[variant],
      sizeClasses[size],
      className,
    );

    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{ className?: string }>;

      return React.cloneElement(child, {
        className: cn(buttonClassName, child.props.className),
      });
    }

    return (
      <button
        ref={ref}
        type={type}
        className={buttonClassName}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
